import React, { createContext, useState, useContext, ReactNode } from 'react';
import Snackbar from '@mui/material/Snackbar';
import Slide, { SlideProps } from '@mui/material/Slide';
import { colors } from '../../constants/colors';

function SlideTransition(props: SlideProps) {
    return <Slide {...props} direction="up" />;
}

type SnackbarMessage = {
    id: number;
    message: string;
};

type SnackbarContextType = {
    showSnackbar: (message: string) => void;
};

const SnackbarContext = createContext<SnackbarContextType | undefined>(undefined);

type SnackbarProviderProps = {
    children: ReactNode;
};

export const SnackbarProvider: React.FC<SnackbarProviderProps> = ({ children }) => {
    const [snackBars, setSnackBars] = useState<SnackbarMessage[]>([]);

    const showSnackbar = (message: string) => {
        const id = new Date().getTime();
        setSnackBars((prev) => [...prev, { id, message }]);
    };

    const handleCloseSnackbar = (id: number) => {
        setSnackBars((prev) => prev.filter((snackbar) => snackbar.id !== id));
    };

    return (
        <SnackbarContext.Provider value={{ showSnackbar }}>
            {children}
            {snackBars.map((snackbar) => (
                <Snackbar
                    key={snackbar.id}
                    ContentProps={{
                        sx: {
                            backgroundColor: colors.themeMainColor,
                            color: colors.white,
                        },
                    }}
                    transitionDuration={1000}
                    open={true}
                    onClose={() => handleCloseSnackbar(snackbar.id)}
                    TransitionComponent={SlideTransition}
                    message={snackbar.message}
                    autoHideDuration={3000}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                />
            ))}
        </SnackbarContext.Provider>
    );
};

export const useSnackbar = (): SnackbarContextType => {
    const context = useContext(SnackbarContext);
    if (!context) {
        throw new Error('useSnackbar must be used within a SnackbarProvider');
    }
    return context;
};
